import React, { useEffect, useMemo, useState } from 'react';
import {
  CssBaseline,
  IconButton,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Avatar,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Person, ReceiptLongOutlined } from '@mui/icons-material';
import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Logo from '../../../assets/images/Voxtur-verify.png';
import { configuration } from '../../../store/slices/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumb from '../../common/Breadcrumb';
import { useLocation, useNavigate } from 'react-router';
import Loader from '../../common/Loader';
import { getConfigurations } from '../../../services/auth/auth';
import ProfileDrawer from '../../common/ProfileDrawer';
import LogoComponent from '../../admin/mainLayout/Logo';

const drawerWidth = 270;
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});
const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `${window.innerWidth < 768 ? '0px' : `calc(${theme.spacing(4)}})`}`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(5)})`,
  },
});
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2, 1),
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

const RxMainLayout = ({ children }) => {
  const url = window.location.origin;
  const location = useLocation();
  const config = useSelector((state) => state.authSlice)?.configuration?.[url];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const logoUrl = useMemo(() => config?.company_logo_url || Logo, [config?.company_logo_url]);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      const res = await getConfigurations({ domain: url });
      if (res.success) {
        const activeOffices = res?.response?.filter((ofc) => ofc.status !== 'disabled');
        dispatch(configuration({ url, data: { ...(activeOffices?.[0] || {}) } }));
      }
      setLoading(false);
    };
    loadConfig();
  }, []);

  return (
    <>
      {loading && <Loader />}
      <CssBaseline />
      <div className='main-layout-div'>
        <Drawer variant='permanent' open={open} className='order-dashboard-sidebar modern-sidebar'>
          <DrawerHeader>
            {open ? (
              <div className='d-flex align-items-center justify-between w-100p'>
                <LogoComponent src={logoUrl} />
                <IconButton onClick={() => setOpen(false)} className='toggle-btn' size='small'>
                  <ChevronLeft fontSize='small' />
                </IconButton>
              </div>
            ) : (
              <IconButton onClick={() => setOpen(true)} className='toggle-btn-collapsed' size='small'>
                <ChevronRight fontSize='small' />
              </IconButton>
            )}
          </DrawerHeader>
          <Divider />
          <div className={`${open ? 'list-item-wrapper' : ''}`}>
            <ListItem
              button
              onClick={() => navigate('/rx/orders')}
              className={`${location?.pathname?.includes('/rx/orders') ? 'selected-item' : ''} sidebar-item`}
            >
              <ListItemIcon>
                <ReceiptLongOutlined fontSize='small' />
              </ListItemIcon>
              <ListItemText primary='Orders' style={{ fontSize: '14px' }} />
            </ListItem>
          </div>
        </Drawer>
        <div className='main-layout-toolbar-div'>
          <Toolbar className='header-title main-layout-toolbar'>
            <div className='main-toolbar-block main-toolbar-left d-flex align-items-center'>
              {window.innerWidth > 550 && <Breadcrumb />}
            </div>
            <div className='main-toolbar-block main-toolbar-right d-flex align-items-center'>
              <Avatar
                className='cursor-pointer'
                onClick={() => setDrawerOpen(true)}
                sx={{ width: 36, height: 36 }}
              >
                <Person />
              </Avatar>
            </div>
          </Toolbar>
          <div className='main-layout-content'>{children}</div>
        </div>
      </div>
      <ProfileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} type='rx' />
    </>
  );
};

export default RxMainLayout;
